import { Delete as DeleteIcon } from "@mui/icons-material";
import Logger from "../../../services/Logger";
import { Checkbox, FormControlLabel, FormGroup, IconButton, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import KeywordBox from "../../components/KeywordBox";
import AdminJigsawPlayService from "./AdminJigsawPlayService";
import { getSolveColumns } from "./getSolveColumns";

export default function AdminJigsawPlay({ jigsawId, storyId, embedded = false, fill = false }) {
    const navigate = useNavigate();
    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 15 });
    const [sort, setSort] = useState([{ field: "startedAt", sort: "desc" }]);
    const [rowCount, setRowCount] = useState(0);
    const [rows, setRows] = useState([]);
    const [keyword, setKeyword] = useState("");
    const [completionStatus, setCompletionStatus] = useState({
        all: true,
        completed: false,
        incomplete: false,
    });
    const [onlyStarted, setOnlyStarted] = useState(false);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [, setSelectedRowDataForDelete] = useState([]);

    useEffect(() => {
        const isCompleted = completionStatus.completed ? true : completionStatus.incomplete ? false : null;
        AdminJigsawPlayService.requestPuzzlePlay(
            jigsawId,
            keyword,
            pageModel,
            sort,
            setRows,
            setRowCount,
            isCompleted,
            onlyStarted,
            storyId,
        );
    }, [pageModel, sort, jigsawId, storyId, keyword, completionStatus, onlyStarted]);

    const handleRowClick = (params, event) => {
        if (event.target.closest(".MuiCheckbox-root, .MuiDataGrid-cellCheckbox")) {
            return;
        }
        navigate(`/admin/jigsaw/play/${params.row.id}`);
    };

    const handleSort = (model) => {
        setSort(model);
    };

    const handleSelectionModel = (newSelection) => {
        setSelectedRowIds(newSelection);
        const selectedRowData = rows.filter((row) => newSelection.includes(row.id));
        setSelectedRowDataForDelete(selectedRowData);
    };

    const handleCompletionStatusChange = (status) => {
        setCompletionStatus((prev) => {
            const newStatus = { ...prev };

            if (status === "all") {
                newStatus.all = true;
                newStatus.completed = false;
                newStatus.incomplete = false;
            } else {
                newStatus.all = false;
                newStatus[status] = !prev[status];
            }

            return newStatus;
        });
    };

    const handleDeleteClick = () => {
        if (selectedRowIds.length > 0) {
            setDeleteDialogOpen(true);
        }
    };

    const handleDeleteConfirm = async (targetIds) => {
        try {
            await AdminJigsawPlayService.deleteJigsawPlay(targetIds);
            const isCompleted = completionStatus.completed ? true : completionStatus.incomplete ? false : null;
            await AdminJigsawPlayService.requestPuzzlePlay(
                jigsawId,
                keyword,
                pageModel,
                sort,
                setRows,
                setRowCount,
                isCompleted,
                onlyStarted,
                storyId,
            );
            setSelectedRowIds([]);
            setSelectedRowDataForDelete([]);
        } catch (error) {
            Logger.error("Error deleting jigsaw play:", error);
        }
    };

    const columns_solve = getSolveColumns();

    return (
        <Box
            sx={{
                flexGrow: 1,
                height: fill ? "100%" : embedded ? 440 : "50%",
                minHeight: fill ? 0 : embedded ? 360 : undefined,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <KeywordBox callback={setKeyword} />
                    <FormGroup row>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={completionStatus.all}
                                    onChange={() => handleCompletionStatusChange("all")}
                                    color="primary"
                                />
                            }
                            label="All"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={completionStatus.completed}
                                    onChange={() => handleCompletionStatusChange("completed")}
                                    color="primary"
                                />
                            }
                            label="Completed"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={completionStatus.incomplete}
                                    onChange={() => handleCompletionStatusChange("incomplete")}
                                    color="primary"
                                />
                            }
                            label="Incomplete"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={onlyStarted}
                                    onChange={(e) => setOnlyStarted(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Only started"
                        />
                    </FormGroup>
                </Box>
                <Tooltip title={`Delete ${selectedRowIds.length} selected row${selectedRowIds.length !== 1 ? "s" : ""}`}>
                    <span>
                        <IconButton
                            color="error"
                            onClick={handleDeleteClick}
                            disabled={selectedRowIds.length === 0}
                            sx={{ opacity: selectedRowIds.length > 0 ? 1 : 0.5 }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            <Box sx={{ flex: fill ? 1 : undefined, minHeight: fill ? 0 : undefined, width: "100%" }}>
            <DataGrid
                rows={rows}
                columns={columns_solve}
                rowCount={rowCount}
                pageSizeOptions={[15, 25, 30]}
                paginationMode="server"
                sortingMode="server"
                pagination={true}
                paginationModel={pageModel}
                onPaginationModelChange={(m) => setPageModel(m)}
                checkboxSelection
                experimentalFeatures={{ newEditingApi: false }}
                onRowClick={handleRowClick}
                onSortModelChange={handleSort}
                onRowSelectionModelChange={handleSelectionModel}
                columnVisibilityModel={{
                    id: false,
                    name: false,
                    ...(storyId ? { puzzleTitle: false } : {}),
                }}
                hideFooterSelectedRowCount
                sx={{
                    height: fill ? "100%" : embedded ? 360 : undefined,
                    "& .MuiDataGrid-row": { cursor: "pointer" },
                }}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                            name: false,
                        },
                    },
                }}
            />
            </Box>

            <ConfirmDialog
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                title="Confirm Delete"
                message={`Are you sure you want to delete ${selectedRowIds.length} selected puzzle play record${selectedRowIds.length !== 1 ? "s" : ""}? This action cannot be undone.`}
                warningAlert="Don't use for user's play history. For testing purpose only."
                targetId={selectedRowIds}
                callback={handleDeleteConfirm}
            />
        </Box>
    );
}
